// attribution.js — sanitized first/latest acquisition touches (contract §4).
//
// The landing page is where external acquisition (utm_*, ad click ids,
// referrer) actually arrives, so this is where it is read. What is read is
// reduced immediately to the allowlist: five utm_* values, three click-id
// presence booleans, a referrer *host* and a redacted landing path. The raw
// query string and raw referrer are never stored or sent.
//
// Storage rule: touches are written to localStorage ONLY while analytics
// consent is granted. Before consent the parsed touch lives in memory for the
// current page view, so a visitor who accepts after arriving still gets the
// touch they arrived with, and a visitor who never accepts leaves nothing
// behind. Withdrawal deletes both keys.

import {
  UTM_KEYS,
  CLICK_ID_KEYS,
  normalizeAttributionValue,
  referrerHost,
  redactPath,
} from './sanitize';

export const FIRST_KEY = 'cv.attr.first.v1';
export const LATEST_KEY = 'cv.attr.latest.v1';

const TOUCH_KEYS = [
  ...UTM_KEYS,
  'has_gclid',
  'has_twclid',
  'has_fbclid',
  'referrer_host',
  'landing_path',
];

/**
 * Build a sanitized Touch from a query string, referrer and pathname.
 * Pure: pass the pieces in explicitly (tests) or let it read `window`.
 */
export function parseTouch({ search, referrer, pathname } = {}) {
  const touch = {};
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    params = new URLSearchParams();
  }
  for (const key of UTM_KEYS) {
    if (!params.has(key)) continue;
    const value = normalizeAttributionValue(params.get(key));
    if (value) touch[key] = value;
  }
  for (const key of CLICK_ID_KEYS) {
    const raw = params.get(key);
    if (raw && raw.trim()) touch[`has_${key}`] = true;
  }
  const ref = referrerHost(referrer || '');
  if (ref) touch.referrer_host = ref;
  if (pathname) touch.landing_path = redactPath(String(pathname).split(/[?#]/)[0]).slice(0, 200);
  return touch;
}

/** An external acquisition key is any allowlisted utm_* or a click id. */
export function hasAcquisitionKey(touch) {
  if (!touch) return false;
  return UTM_KEYS.some((k) => touch[k]) || CLICK_ID_KEYS.some((k) => touch[`has_${k}`]);
}

/** Re-validate a stored touch (localStorage is user-writable). */
export function cleanTouch(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  for (const key of TOUCH_KEYS) {
    const value = raw[key];
    if (value == null) continue;
    if (key.startsWith('has_')) {
      if (value === true) out[key] = true;
    } else if (key === 'referrer_host') {
      if (value === 'direct' || value === 'internal') out[key] = value;
      else if (typeof value === 'string' && /^[a-z0-9.-]{1,100}$/.test(value)) out[key] = value;
    } else if (key === 'landing_path') {
      if (typeof value === 'string' && value.startsWith('/')) {
        out[key] = redactPath(value.split(/[?#]/)[0]).slice(0, 200);
      }
    } else {
      const clean = normalizeAttributionValue(value);
      if (clean) out[key] = clean;
    }
  }
  return Object.keys(out).length ? out : null;
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function read(key) {
  try {
    const raw = storage()?.getItem(key);
    return raw ? cleanTouch(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function write(key, touch) {
  try {
    storage()?.setItem(key, JSON.stringify(touch));
  } catch {
    /* storage full or blocked; attribution is best-effort */
  }
}

// The touch this page view arrived with, parsed once at module load. Memory
// only — see the header comment.
let pageTouch = null;

export function capturePageTouch(loc = typeof window !== 'undefined' ? window.location : null,
  doc = typeof document !== 'undefined' ? document : null) {
  try {
    pageTouch = parseTouch({
      search: loc?.search,
      referrer: doc?.referrer,
      pathname: loc?.pathname,
    });
  } catch {
    pageTouch = null;
  }
  return pageTouch;
}

export function getPageTouch() {
  return pageTouch;
}

/**
 * Persist this page view's touch. Caller MUST only call this with consent.
 * `first` is written once; `latest` is overwritten when the touch carries an
 * external acquisition key.
 */
export function persistTouch(touch = pageTouch) {
  if (!touch || !Object.keys(touch).length) return;
  if (!read(FIRST_KEY)) write(FIRST_KEY, touch);
  if (hasAcquisitionKey(touch)) write(LATEST_KEY, touch);
}

export function getFirstTouch() {
  return read(FIRST_KEY);
}

export function getLatestTouch() {
  return read(LATEST_KEY);
}

export function clearTouches() {
  try {
    storage()?.removeItem(FIRST_KEY);
    storage()?.removeItem(LATEST_KEY);
  } catch {
    /* nothing stored */
  }
}

/**
 * Map a first touch to the `cv_ft_*` link params (§4). Only called when
 * consent is granted at click time. Presence booleans become `1`.
 */
export function firstTouchParams(touch) {
  const clean = cleanTouch(touch);
  if (!clean) return {};
  const out = {};
  for (const key of UTM_KEYS) {
    if (clean[key]) out[`cv_ft_${key.slice(4)}`] = clean[key];
  }
  if (clean.referrer_host) out.cv_ft_ref = clean.referrer_host;
  if (clean.has_gclid) out.cv_ft_gclid = '1';
  if (clean.has_twclid) out.cv_ft_twclid = '1';
  return out;
}
